/**
 * What the browser panel mounts, as data rather than as JSX branching.
 *
 * `BrowserSurfaceHost` used to decide this inline, and the decision was split
 * across two unrelated sources: `presentation.*` for the live surfaces, and
 * `previewVm.canRenderCanvas` for the picture. Because the second one answered
 * "is there a frame or screenshot to paint?" and nothing else, a session whose
 * live transport had died still painted the last screencast frame — the user saw
 * a browser, and had no way to tell it was not the live one.
 *
 * Splitting the plan out gives that decision a seam a test can hold, and lets
 * the two jobs the old canvas branch conflated come apart: `commandInput` is the
 * layer that turns a click into a runtime command, and `paintsCapturedFrame` is
 * the picture. Only the picture was the legacy route, which is why removing it
 * costs no session its input.
 *
 * @module browserSurfaceMounts
 */
import type { BrowserPresentationDecision } from "@cmmd-forge/shared/browserPresentation";

/**
 * A discriminated union rather than a shell plus a bag of booleans.
 *
 * The flat shape could describe combinations that cannot exist — a `status`
 * shell with a live video mounted, an `annotation` shell capturing input — and
 * was kept honest only by the resolver happening to be correct. Hanging the
 * live-surface flags off the one arm where they vary makes those states
 * unrepresentable instead of merely unreached, and tells a reader which fields
 * are meaningful for which shell. `.agents/rules/forge-react-patterns` asks for
 * exactly this over optional booleans.
 *
 * The other two arms carry no fields at all: `annotation` paints a still by
 * definition, and `status` paints nothing, so a boolean saying so was noise
 * that could disagree with the shell it travelled with.
 */
export type BrowserSurfaceMountPlan =
  | {
      readonly shell: "interactive";
      /** The input-capture layer. Mounted under a stream, withheld from a native overlay. */
      readonly commandInput: boolean;
      /** The OS-level Chromium view, desktop-local only. */
      readonly nativeOverlay: boolean;
      /** The WebRTC `<video>` element. */
      readonly remoteVideo: boolean;
    }
  | { readonly shell: "annotation" }
  | { readonly shell: "status" };

/**
 * Whether this plan paints a stored frame or screenshot into the panel.
 *
 * True for the annotation shell alone, where a still is what the user asked
 * for. It is a function of the shell rather than a field on it, which is the
 * point: an interactive plan cannot carry a `true` here, and that combination
 * is the legacy screencast fallback this module exists to prevent.
 */
export function browserSurfacePaintsCapturedFrame(plan: BrowserSurfaceMountPlan): boolean {
  return plan.shell === "annotation";
}

export function resolveBrowserSurfaceMounts(input: {
  readonly presentation: BrowserPresentationDecision;
  /** Whether the panel has a frame or screenshot in hand at all. */
  readonly canRenderCanvas: boolean;
}): BrowserSurfaceMountPlan {
  const { presentation } = input;

  if (!presentation.interactive) {
    return input.canRenderCanvas ? { shell: "annotation" } : { shell: "status" };
  }

  // The two real transports are the only things that may claim the interactive
  // shell. Stating it that way rather than listing the failures means a new
  // surface kind cannot quietly inherit the right to paint over a dead lane.
  const liveTransportUp =
    presentation.primary === "native-overlay" || presentation.primary === "remote-pixel";
  if (!liveTransportUp) {
    return { shell: "status" };
  }

  return {
    shell: "interactive",
    commandInput: presentation.commandInputActive,
    nativeOverlay: presentation.nativeOverlayActive,
    remoteVideo: presentation.remotePixelActive,
  };
}
